import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';

import { LotRecommendationType } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';

import { ItemCardInfo } from '../types/types';

type SpacingToken = 'lg' | 'md' | 'sm' | 'xl' | 'xs';
type BreakpointToken = 'fluidGridXsMax' | 'lgMin' | 'mdMin' | 'smMin';
type GapConfig = { x?: SpacingToken; y?: SpacingToken };

export type GridBreakpoints = {
    colsByBreakpoint: { base: number } & Partial<Record<BreakpointToken, number>>;
    gapsByBreakpoint?: { base?: GapConfig } & Partial<Record<BreakpointToken, GapConfig>>;
};

// fluidGridXsMax is a custom max-width screen (@custom-variant in src/tailwind.css) scoped
// with this unusual name specifically to avoid colliding with hammer-ui-theme's
// reserved screen names (e.g. its own real `xsMax: { max: '768px' }`, used by
// hammer-ui-core's CardGrid) — reusing one of those names here silently
// overwrote its value app-wide last time. It's max-width based (not min-width)
// so its range never overlaps with smMin/mdMin/lgMin, since Tailwind doesn't
// reliably order this app-level custom screen against the plugin's screens in
// the compiled CSS.
const COLS_CLASSES: Record<number, [string, string, string, string, string]> = {
    1: ['grid-cols-1', 'fluidGridXsMax:grid-cols-1', 'smMin:grid-cols-1', 'mdMin:grid-cols-1', 'lgMin:grid-cols-1'],
    2: ['grid-cols-2', 'fluidGridXsMax:grid-cols-2', 'smMin:grid-cols-2', 'mdMin:grid-cols-2', 'lgMin:grid-cols-2'],
    3: ['grid-cols-3', 'fluidGridXsMax:grid-cols-3', 'smMin:grid-cols-3', 'mdMin:grid-cols-3', 'lgMin:grid-cols-3'],
    4: ['grid-cols-4', 'fluidGridXsMax:grid-cols-4', 'smMin:grid-cols-4', 'mdMin:grid-cols-4', 'lgMin:grid-cols-4'],
    6: ['grid-cols-6', 'fluidGridXsMax:grid-cols-6', 'smMin:grid-cols-6', 'mdMin:grid-cols-6', 'lgMin:grid-cols-6'],
};

const GAP_X_CLASSES: Record<SpacingToken, [string, string, string, string, string]> = {
    xs: ['gap-x-xs', 'fluidGridXsMax:gap-x-xs', 'smMin:gap-x-xs', 'mdMin:gap-x-xs', 'lgMin:gap-x-xs'],
    sm: ['gap-x-sm', 'fluidGridXsMax:gap-x-sm', 'smMin:gap-x-sm', 'mdMin:gap-x-sm', 'lgMin:gap-x-sm'],
    md: ['gap-x-md', 'fluidGridXsMax:gap-x-md', 'smMin:gap-x-md', 'mdMin:gap-x-md', 'lgMin:gap-x-md'],
    lg: ['gap-x-lg', 'fluidGridXsMax:gap-x-lg', 'smMin:gap-x-lg', 'mdMin:gap-x-lg', 'lgMin:gap-x-lg'],
    xl: ['gap-x-xl', 'fluidGridXsMax:gap-x-xl', 'smMin:gap-x-xl', 'mdMin:gap-x-xl', 'lgMin:gap-x-xl'],
};

const GAP_Y_CLASSES: Record<SpacingToken, [string, string, string, string, string]> = {
    xs: ['gap-y-xs', 'fluidGridXsMax:gap-y-xs', 'smMin:gap-y-xs', 'mdMin:gap-y-xs', 'lgMin:gap-y-xs'],
    sm: ['gap-y-sm', 'fluidGridXsMax:gap-y-sm', 'smMin:gap-y-sm', 'mdMin:gap-y-sm', 'lgMin:gap-y-sm'],
    md: ['gap-y-md', 'fluidGridXsMax:gap-y-md', 'smMin:gap-y-md', 'mdMin:gap-y-md', 'lgMin:gap-y-md'],
    lg: ['gap-y-lg', 'fluidGridXsMax:gap-y-lg', 'smMin:gap-y-lg', 'mdMin:gap-y-lg', 'lgMin:gap-y-lg'],
    xl: ['gap-y-xl', 'fluidGridXsMax:gap-y-xl', 'smMin:gap-y-xl', 'mdMin:gap-y-xl', 'lgMin:gap-y-xl'],
};

const BREAKPOINT_INDEX: Record<'base' | BreakpointToken, number> = {
    base: 0,
    fluidGridXsMax: 1,
    smMin: 2,
    mdMin: 3,
    lgMin: 4,
};

function getGridClasses(
    colsByBreakpoint: GridBreakpoints['colsByBreakpoint'],
    gapsByBreakpoint: GridBreakpoints['gapsByBreakpoint']
) {
    const classes = ['grid', 'justify-center', 'w-full'];

    (Object.keys(BREAKPOINT_INDEX) as Array<'base' | BreakpointToken>).forEach((key) => {
        const cols = colsByBreakpoint[key as 'base' | BreakpointToken];
        if (cols) {
            classes.push(COLS_CLASSES[cols][BREAKPOINT_INDEX[key]]);
        }

        const gap = gapsByBreakpoint?.[key as 'base' | BreakpointToken];
        if (gap?.x) {
            classes.push(GAP_X_CLASSES[gap.x][BREAKPOINT_INDEX[key]]);
        }
        if (gap?.y) {
            classes.push(GAP_Y_CLASSES[gap.y][BREAKPOINT_INDEX[key]]);
        }
    });

    return classes;
}

type Props = {
    breakpoints?: GridBreakpoints;
    className?: string;
    itemCardOption: ItemCardInfo;
    itemIds: number[];
    lazyLoadImage?: boolean;
    page?: number;
    pageSize?: number;
    recommendationType?: LotRecommendationType | null;
    trackVisible?: (itemId: number, isPromoted: boolean) => void;
};

export default function FluidItemGrid({
    breakpoints = DEFAULT_BREAKPOINTS,
    className,
    itemCardOption,
    itemIds,
    lazyLoadImage,
    page,
    pageSize,
    recommendationType,
    trackVisible,
}: Props) {
    const { ItemCardComponent } = itemCardOption;

    const gridClassName = useMemo(
        () => cn(getGridClasses(breakpoints.colsByBreakpoint, breakpoints.gapsByBreakpoint), className),
        [breakpoints, className]
    );

    const getPagePosition = (itemId: number) => {
        if (Boolean(page) && Boolean(pageSize)) {
            return (page - 1) * pageSize + (itemIds.indexOf(itemId) + 1);
        } else {
            return 0;
        }
    };

    return (
        <div
            className={gridClassName}
            data-testid="ItemGridContainer"
        >
            {itemIds.map((itemId, index) => {
                const position = index + 1;

                return (
                    <ItemCardComponent
                        itemId={itemId}
                        key={`${itemId}-${index}`}
                        lazyLoadImage={!lazyLoadImage && index <= 3 ? false : true}
                        pagePosition={getPagePosition(itemId)}
                        recommendationPosition={position}
                        recommendationType={recommendationType}
                        trackVisible={trackVisible}
                    />
                );
            })}
        </div>
    );
}

const DEFAULT_BREAKPOINTS: GridBreakpoints = {
    colsByBreakpoint: {
        base: 2,
        fluidGridXsMax: 1,
        smMin: 4,
    },
    gapsByBreakpoint: {
        base: { x: 'sm', y: 'sm' },
        smMin: { x: 'md', y: 'lg' },
    },
};
